import { Check, ChevronDown, CircleAlert, LoaderCircle } from 'lucide-react'
import * as React from 'react'

import { Alert, AlertTitle } from '@/components/ui/alert'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'

import { LinkButton } from '../LinkButton'
import type { SnippetImpactDuty } from '../types'
import { plural } from '../utils'
import type { ImpactStrip } from './model'

function dutyLabel(duty: SnippetImpactDuty): string {
  return duty.org_slug ? `${duty.org_slug} · ${duty.duty_name}` : duty.duty_name
}

type Tone = 'idle' | 'ok' | 'bad' | 'stale'

const TONE: Record<ImpactStrip['state'], Tone> = {
  idle: 'idle',
  checking: 'idle',
  ok: 'ok',
  bad: 'bad',
  stale: 'stale',
  error: 'bad',
}

const TONE_CLASS: Record<Tone, string> = {
  idle: 'bg-app text-muted-foreground',
  ok: 'bg-success-ui-bg text-success-solid',
  bad: 'bg-error-ui-bg text-error-solid',
  stale: 'bg-warning-ui-bg text-warning-low-contrast',
}

const DOT = (
  <span
    aria-hidden="true"
    className="size-2 shrink-0 rounded-full bg-neutral-solid"
  />
)
const ICON: Record<ImpactStrip['state'], React.ReactNode> = {
  idle: DOT,
  checking: <LoaderCircle aria-hidden="true" className="animate-spin" />,
  ok: <Check aria-hidden="true" />,
  bad: <CircleAlert aria-hidden="true" />,
  stale: <CircleAlert aria-hidden="true" />,
  error: <CircleAlert aria-hidden="true" />,
}

function message(strip: ImpactStrip, reach: string): string {
  switch (strip.state) {
    case 'idle':
      return `This edit changes ${reach}`
    case 'checking':
      return `Checking ${reach}…`
    case 'ok':
      return 'Every referencing duty can render this text'
    case 'stale':
      return 'Text changed since the last check'
    case 'error':
      return strip.message
    case 'bad': {
      const { duties } = strip.impact
      const failing = duties.filter((duty) => !duty.ok).length
      return `${failing} of ${plural(duties.length, 'duty')} cannot render this text`
    }
  }
}

/**
 * Advisory, one line above the footer. Editing a snippet changes every duty
 * using it, and a body that reads a binding one of those duties cannot
 * resolve breaks its calls at dispatch. The strip names the reach, the verdict
 * after a check, and opens the issue list only when there is one. It never
 * blocks the save; for a shared snippet, Save runs the check first and asks
 * before proceeding on a failure.
 */
export function SnippetImpactPanel({
  reach,
  strip,
  onCheck,
}: {
  /** Who a save reaches: "3 duties", "7 duties in 2 orgs". */
  reach: string
  strip: ImpactStrip
  onCheck: () => void
}) {
  const failing =
    strip.state === 'bad' ? strip.impact.duties.filter((duty) => !duty.ok) : []
  // Opens with each failing result, so the author sees why without a click;
  // closing it is remembered until the next check.
  const result = strip.state === 'bad' ? strip.impact : null
  const [showIssues, setShowIssues] = React.useState(true)
  const [lastResult, setLastResult] = React.useState(result)
  if (result !== lastResult) {
    setLastResult(result)
    setShowIssues(true)
  }
  const text = message(strip, reach)

  return (
    <Collapsible
      open={failing.length > 0 && showIssues}
      onOpenChange={setShowIssues}
      className="shrink-0 border-t border-neutral-subtle-border">
      <Alert
        role="status"
        data-impact-tone={TONE[strip.state]}
        className={cn(
          'flex min-h-10 items-center gap-2.5 rounded-none border-0 px-4 py-2',
          TONE_CLASS[TONE[strip.state]]
        )}>
        {ICON[strip.state]}
        <AlertTitle
          className="min-w-0 flex-1 font-normal tracking-normal"
          title={text}>
          {text}
        </AlertTitle>
        {failing.length > 0 && (
          <CollapsibleTrigger asChild>
            <LinkButton className="text-current">
              {showIssues ? 'Hide' : 'Show'} {plural(failing.length, 'issue')}
              <ChevronDown
                aria-hidden="true"
                className={cn(
                  'size-3.5 transition-transform',
                  showIssues && 'rotate-180'
                )}
              />
            </LinkButton>
          </CollapsibleTrigger>
        )}
        {strip.state !== 'checking' && (
          <LinkButton className="text-current" onClick={onCheck}>
            {strip.state === 'idle' ? 'Check impact' : 'Re-check'}
          </LinkButton>
        )}
      </Alert>
      <CollapsibleContent>
        <ul className="max-h-32 space-y-1 overflow-y-auto border-t border-neutral-subtle-border bg-error-ui-bg px-4 py-2 text-sm">
          {failing.map((duty) => (
            <li key={`${duty.org_slug ?? ''}:${duty.duty_id}`}>
              <span className="font-medium">{dutyLabel(duty)}</span>
              {`: ${duty.issues.join('; ')}`}
            </li>
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  )
}
